import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useKeycloakStore } from './keycloak.js'

function buildPath(groupId, suffix) {
  return import.meta.env.VITE_APP_URL + `groups/${groupId}/calendar/${suffix}`
}

async function request(config) {
  const { default: axios } = await import('axios')
  return axios({
    ...config,
    headers: {
      ...(config.headers || {}),
      Authorization: `Bearer ${useKeycloakStore().token}`,
    },
  })
}

export const useCalendarStore = defineStore('calendar', () => {
  const eventsByGroup = ref({})
  const remindersByEvent = ref({})
  const loading = ref(false)
  const loadedGroups = ref(new Set())

  function getEvents(groupId) {
    return eventsByGroup.value[groupId] || []
  }

  function ensureEvents(groupId) {
    if (!eventsByGroup.value[groupId]) {
      eventsByGroup.value[groupId] = []
    }
    return eventsByGroup.value[groupId]
  }

  function getReminders(eventId) {
    return remindersByEvent.value[eventId] || []
  }

  function ensureReminders(eventId) {
    if (!remindersByEvent.value[eventId]) {
      remindersByEvent.value[eventId] = []
    }
    return remindersByEvent.value[eventId]
  }

  function findEvent(groupId, eventId) {
    return getEvents(groupId).find((e) => e.id === eventId)
  }

  async function fetchEvents(groupId, from, to) {
    loading.value = true
    try {
      const { data } = await request({
        url: buildPath(groupId, 'events'),
        method: 'GET',
        params: { from, to },
      })
      eventsByGroup.value[groupId] = data
      loadedGroups.value.add(groupId)
      return data
    } finally {
      loading.value = false
    }
  }

  async function fetchAttendanceSummary(groupId, eventId) {
    const { data } = await request({
      url: buildPath(groupId, `events/${eventId}/attendance/summary`),
      method: 'GET',
    })
    const event = findEvent(groupId, eventId)
    if (event) event.summary = data
    return data
  }

  async function setAttendance(groupId, eventId, status) {
    const { data } = await request({
      url: buildPath(groupId, `events/${eventId}/attendance`),
      method: 'POST',
      data: { status },
    })
    await fetchAttendanceSummary(groupId, eventId)
    return data
  }

  async function createEvent(groupId, payload) {
    const { data } = await request({
      url: buildPath(groupId, 'events'),
      method: 'POST',
      data: payload,
    })
    ensureEvents(groupId).unshift(data)
    return data
  }

  async function updateEvent(groupId, eventId, payload) {
    const { data } = await request({
      url: buildPath(groupId, `events/${eventId}`),
      method: 'PUT',
      data: payload,
    })
    const idx = ensureEvents(groupId).findIndex((e) => e.id === eventId)
    if (idx !== -1) ensureEvents(groupId)[idx] = data
    return data
  }

  async function cancelEvent(groupId, eventId) {
    const { data } = await request({
      url: buildPath(groupId, `events/${eventId}/cancel`),
      method: 'POST',
    })
    const idx = ensureEvents(groupId).findIndex((e) => e.id === eventId)
    if (idx !== -1) ensureEvents(groupId)[idx] = data
    return data
  }

  async function fetchEventReminders(groupId, eventId) {
    const { data } = await request({
      url: buildPath(groupId, `events/${eventId}/reminders`),
      method: 'GET',
    })
    const list = Array.isArray(data) ? data : []
    remindersByEvent.value[eventId] = list
    const event = findEvent(groupId, eventId)
    if (event) event.reminders = list
    return list
  }

  async function addReminder(groupId, eventId, payload) {
    const { data } = await request({
      url: buildPath(groupId, `events/${eventId}/reminders`),
      method: 'POST',
      data: payload,
    })
    ensureReminders(eventId).push(data)
    const event = findEvent(groupId, eventId)
    if (event) event.reminders = getReminders(eventId)
    return data
  }

  async function deleteReminder(groupId, eventId, reminderId) {
    await request({
      url: buildPath(groupId, `events/${eventId}/reminders/${reminderId}`),
      method: 'DELETE',
    })
    remindersByEvent.value[eventId] = getReminders(eventId).filter((r) => r.id !== reminderId)
    const event = findEvent(groupId, eventId)
    if (event) event.reminders = remindersByEvent.value[eventId]
  }

  async function setReminders(groupId, eventId, reminders) {
    const { data } = await request({
      url: buildPath(groupId, `events/${eventId}/reminders`),
      method: 'PUT',
      data: reminders,
    })
    const list = Array.isArray(data) ? data : reminders
    remindersByEvent.value[eventId] = list
    const event = findEvent(groupId, eventId)
    if (event) event.reminders = list
    return list
  }

  async function downloadIcs(groupId, event) {
    const res = await request({
      url: buildPath(groupId, `events/${event.id}/ics`),
      method: 'GET',
      responseType: 'blob',
    })
    const fileUrl = URL.createObjectURL(res.data)
    const a = document.createElement('a')
    a.href = fileUrl
    a.download = `avocash-${event.title || 'event'}.ics`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(fileUrl)
    return res.data
  }

  return {
    eventsByGroup,
    remindersByEvent,
    loading,
    loadedGroups,
    getEvents,
    getReminders,
    ensureEvents,
    ensureReminders,
    fetchEvents,
    fetchAttendanceSummary,
    setAttendance,
    createEvent,
    updateEvent,
    cancelEvent,
    fetchEventReminders,
    addReminder,
    deleteReminder,
    setReminders,
    downloadIcs,
  }
})
